import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react'

export type CardVariant = 'glass' | 'elevated' | 'outline'
export type CardAccent = 'none' | 'primary' | 'success' | 'warn' | 'danger'

type Props = {
  variant?: CardVariant
  accent?: CardAccent
  interactive?: boolean
  onClick?: () => void
  eyebrow?: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode
  chip?: ReactNode
  media?: ReactNode
  footer?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  className?: string
  style?: CSSProperties
  ariaLabel?: string
  disabled?: boolean
}

/**
 * Common premium Card. One primitive covers checklist cards, template cards,
 * dashboard tiles. Interactive cards behave like buttons (Enter/Space activate,
 * focus ring, hover lift).
 */
export const Card = ({
  variant = 'glass',
  accent = 'primary',
  interactive = false,
  onClick,
  eyebrow,
  title,
  subtitle,
  icon,
  chip,
  media,
  footer,
  actions,
  children,
  className,
  style,
  ariaLabel,
  disabled = false,
}: Props) => {
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled || !interactive) return
    onClick?.()
    event.stopPropagation()
  }
  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive || disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick?.()
    }
  }

  return (
    <div
      className={[
        'zapCard',
        `zapCard--${variant}`,
        `zapCard--accent-${accent}`,
        interactive ? 'is-interactive' : '',
        disabled ? 'is-disabled' : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      style={style}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive && !disabled ? 0 : undefined}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKey : undefined}
    >
      {accent !== 'none' && <span className="zapCard__accent" aria-hidden />}
      {media && <div className="zapCard__media">{media}</div>}
      {(eyebrow || title || subtitle || icon || chip || actions) && (
        <div className="zapCard__header">
          {icon && <span className="zapCard__icon" aria-hidden>{icon}</span>}
          <div className="zapCard__headText">
            {eyebrow && <span className="zapCard__eyebrow">{eyebrow}</span>}
            {title && <span className="zapCard__title">{title}</span>}
            {subtitle && <span className="zapCard__subtitle">{subtitle}</span>}
          </div>
          <div className="zapCard__headRight">
            {chip && <span className="zapCard__chip">{chip}</span>}
            {actions && <span className="zapCard__actions" onClick={(e) => e.stopPropagation()}>{actions}</span>}
          </div>
        </div>
      )}
      {children && <div className="zapCard__body">{children}</div>}
      {footer && <div className="zapCard__footer">{footer}</div>}
    </div>
  )
}
